import { error, log } from '../app/logger.js';
import { services } from '../app/services.js';
import { AsyncDataPipeable } from '../base/pipeable/async_data_pipeable.js';
import { FoodMenuFoods } from './food_menu_foods.js';
import { FoodMenuPipeables } from './food_menu_pipeables.js';

const kFoodChecklistItemTitle = 'food';

class FoodMenu extends AsyncDataPipeable {
  static meals = ['breakfast', 'lunch', 'dinner', 'snacks'];

  static units = [
    'L',
    'g',
    'gal',
    'kL',
    'kg',
    'lb',
    'mL',
    'mg',
    'oz',
    'pc',
    'pkg',
    'pt',
    'qt',
  ];

  static get foods() {
    if (!this.#foods) {
      this.#foods = new FoodMenuFoods();
    }
    return this.#foods;
  }

  get id() {
    return this.#id;
  }

  get dates() {
    if (this.datesCached === undefined) {
      this.datesCached = this.computeDates();
    }
    return this.datesCached;
  }

  getFoodList(day, meal) {
    let menu = this.data && this.data[day];
    return (menu && menu[meal]) || [];
  }

  set(menus) {
    log(`[data] set menu`);
    super.set(menus);
    if (this.pipeablesCached) {
      this.pipeablesCached.foodListsChange();
    }
  }

  async addMenuItem(day, meal, idx) {
    if (!this.data) {
      error(`[data] no data set`);
      return;
    }
    let menu = this.data[day];
    if (!menu) {
      this.data[day] = menu = {};
    }
    let list = menu[meal];
    if (!list) {
      menu[meal] = list = [];
    }

    const entityId = await this.#getId();
    let id = await services.trip.addMenuItem({
      entityId,
      day,
      meal,
      idx,
      food: '',
      quantity: 0,
      unit: '',
    });

    log(
      `[data] add menu item: ${day} day, ${meal} meal, ${idx} index, id: ${id}`
    );

    list[idx] = { id };
    return this.#foodMenuChanged(day, meal, 'notifyPipes');
  }

  async changeMenuItem(day, meal, idx, entry) {
    log(`[data] change menu item: ${day} day, ${meal} meal, ${idx} index`);
    if (!this.data) {
      error(`[data] no data set`);
      return;
    }
    let menu = this.data[day];
    if (!menu) {
      error(`[data] no menu for ${day} day`);
      return;
    }
    let list = menu[meal];
    if (!list) {
      error(`[data] no menu for ${day} day ${meal} meal`);
      return;
    }
    let item = list[idx];
    if (!item) {
      error(`[data] no menu item for ${day} day ${meal} meal at ${idx} index`);
      return;
    }

    const entityId = await this.#getId();
    let data = {
      entityId,
      id: item.id,
    };
    if (item.idx != idx) {
      data.idx = idx;
    }
    if ('food' in entry) {
      data.food = entry.food;
      item.food = data.food;
      if (this.constructor.#foods) {
        this.constructor.#foods.insert(entry.food);
      }
    }
    if ('quantity' in entry) {
      data.quantity = entry.quantity ? parseInt(entry.quantity) : 0;
      item.quantity = data.quantity;
    }
    if ('unit' in entry) {
      data.unit = entry.unit;
      item.unit = data.unit;
    }
    await services.trip.changeMenuItem(data);

    // TODO: pipe notifications are not fired.
    return this.#foodMenuChanged(day, meal);
  }

  async deleteMenuItem(day, meal, idx) {
    log(`[data] remove menu item: ${day} day, ${meal} meal, ${idx} index`);
    if (!this.data) {
      error(`[data] no data set`);
      return;
    }
    let menu = this.data[day];
    if (!menu) {
      error(`[data] no menu for ${day} day`);
      return;
    }
    let list = menu[meal];
    if (!list) {
      error(`[data] no menu for ${day} day ${meal} meal`);
      return;
    }
    let item = list[idx];
    if (!item) {
      error(`[data] no menu item for ${day} day ${meal} meal at ${idx} index`);
      return;
    }

    const entityId = await this.#getId();
    await services.trip.deleteMenuItem(Object.assign({ entityId }, item));

    list.splice(idx, 1);
    return this.#foodMenuChanged(day, meal, 'notifyPipes');
  }

  toString() {
    return `FoodMenu`;
  }

  // implementation
  computeDates() {
    if (!this.#trip) {
      return [];
    }

    let dates = [this.#trip.startDate];
    let date = new Date(this.#trip.startDate);
    for (let i = 1; i < this.#trip.duration; i++) {
      date = new Date(date);
      date.setDate(date.getDate() + 1);
      dates.push(date.toISOString().substring(0, 10));
    }
    return dates;
  }

  constructor(trip, menuIdGetter) {
    super();

    this.#trip = trip;
    this.#idGetter = menuIdGetter;

    this.#trip.pipe(this.#dataMap);
  }

  getLoadPromise() {
    return this.#getId().then(entityId => services.trip.getMenu(entityId));
  }

  get dataClassName() {
    return 'food-menu';
  }

  #adjustDates() {
    this.datesCached = undefined;
    if (this.pipeablesCached) {
      this.pipeablesCached.datesChange();
    }
  }

  async #foodMenuChanged(day, meal, notifyPipes) {
    let items = new Map();
    for (let dayMenu of this.data) {
      for (let meal in dayMenu) {
        let menu = dayMenu[meal];
        for (let item of menu) {
          if (item.food && item.quantity && item.unit) {
            items.has(item.food)
              ? items.get(item.food).push(item)
              : items.set(item.food, [item]);
          }
        }
      }
    }

    let root = {
      children: [],
    };

    let entries = Array.from(items.entries());
    if (entries.length != 0) {
      root.children = [
        {
          name: kFoodChecklistItemTitle,
          children: entries.map(([food, items], idx) => ({
            name: `${food} x ${items.reduce(
              (total, item) => total + item.quantity,
              0
            )} ${items[0].unit}`,
            idx,
          })),
        },
      ];
    }
    await this.#trip.entities.checklist.updateFoodMenuItems(root);

    if (notifyPipes && this.pipeablesCached) {
      this.pipeablesCached.foodListChange(day, meal);
    }
  }

  async #getId() {
    if (!this.#id) {
      this.#id = await this.#idGetter();
    }
    return this.#id;
  }

  static Pipeables = FoodMenuPipeables;

  static #foods;

  #dataMap = {
    dates: () => this.#adjustDates(),
  };

  #id;
  #idGetter;
  #trip;
}

export { FoodMenu };
